// ═══════════════════════════════════════════════════════════════════════════
// PREVIEW FIXTURES — not the implementation.
//
// The real screens read Supabase exactly as they do today. This file exists
// so the prototype has something to render; none of it ships.
// ═══════════════════════════════════════════════════════════════════════════
export interface Tx {
    id: string
    /** Stable id the avatar seeds from — never the display name. */
    seed: string
    kind: "depo" | "retre" | "transfe"
    name: string
    handle: string
    method: string
    nit: string
    amount: number
    statut: "paye" | "complété" | "en_attente" | "approuve" | "rejete"
    at: string
    note?: string
    fee?: number
    rate?: number
    /** Live tracking, mirroring the feed the dashboard reads. Stage times are
     *  DATA, not something the tracker derives — a guessed timestamp on a
     *  money movement is worse than an honest blank, so a stage with no entry
     *  here renders without a time. */
    feed?: TxFeed
}

export interface TxFeed {
    /** HH:MM per stage that has actually happened. */
    stage_history: { stage: StageKey; at: string }[]
    /** When we expect the stage in progress to finish. */
    eta_next?: string
    /** Running late — a distinct state from failed. */
    delayed?: boolean
    /** Plain-language reason, shown when it failed or stalled. */
    reason?: string
    next_step?: string
}

export type StageKey = "resevwa" | "ap_trete" | "voye" | "rive"

export const TXS: Tx[] = [
    { id: "t1", seed: "u-t1", kind: "depo", name: "Naomie Pierre", handle: "@naomiep", method: "Cashapp", nit: "TRF-MSAM4U-SSFY", amount: 36.23, statut: "paye", at: "Jodi a · 14:22", note: "Pou lekòl la", fee: 3.5, rate: 138,
      feed: { stage_history: [{ stage: "resevwa", at: "14:22" }, { stage: "ap_trete", at: "14:25" }, { stage: "voye", at: "14:26" }, { stage: "rive", at: "14:31" }] } },
    { id: "t2", seed: "u-t2", kind: "retre", name: "Jean Baptiste", handle: "509 38 44 2211", method: "MonCash", nit: "TRF-MSAM2K-91XZ", amount: 75, statut: "en_attente", at: "Jodi a · 09:41", fee: 4.99, rate: 140,
      feed: { stage_history: [{ stage: "resevwa", at: "09:41" }], eta_next: "09:55" } },
    { id: "t3", seed: "u-t3", kind: "transfe", name: "Mirlande Joseph", handle: "@mirlandej", method: "Payvit", nit: "TRF-MSALZ7-4KQD", amount: 20, statut: "complété", at: "Yè · 18:05", fee: 3, rate: 138,
      feed: { stage_history: [{ stage: "resevwa", at: "18:05" }, { stage: "ap_trete", at: "18:05" }, { stage: "voye", at: "18:06" }, { stage: "rive", at: "18:06" }] } },
    { id: "t4", seed: "u-t4", kind: "depo", name: "Sarah Smith", handle: "@sarahsmith", method: "Wise", nit: "TRF-MSALP2-77BC", amount: 100, statut: "paye", at: "Yè · 11:30", fee: 6.99, rate: 138,
      feed: { stage_history: [{ stage: "resevwa", at: "11:30" }, { stage: "ap_trete", at: "11:34" }, { stage: "voye", at: "11:35" }, { stage: "rive", at: "11:52" }] } },
    { id: "t5", seed: "u-t5", kind: "retre", name: "Naomie Pierre", handle: "509 38 44 2211", method: "NatCash", nit: "TRF-MSAKX9-2VDM", amount: 50, statut: "approuve", at: "28 jiyè · 16:12", fee: 3.5, rate: 140,
      feed: { stage_history: [{ stage: "resevwa", at: "16:12" }, { stage: "ap_trete", at: "16:20" }], delayed: true, reason: "Operatè a poko konfime resepsyon an.", next_step: "Nou ap reeseye otomatikman." } },
    { id: "t6", seed: "u-t6", kind: "transfe", name: "Kervens Louis", handle: "@kervensl", method: "Payvit", nit: "TRF-MSAKB4-8HTR", amount: 15, statut: "complété", at: "27 jiyè · 08:55", fee: 3, rate: 138,
      feed: { stage_history: [{ stage: "resevwa", at: "08:55" }, { stage: "ap_trete", at: "08:55" }, { stage: "voye", at: "08:56" }, { stage: "rive", at: "08:56" }] } },
    { id: "t7", seed: "u-t7", kind: "depo", name: "Jean Baptiste", handle: "@jeanb", method: "Zelle", nit: "TRF-MSAJM1-3PWL", amount: 45, statut: "rejete", at: "26 jiyè · 20:18", fee: 3.5, rate: 138,
      feed: { stage_history: [{ stage: "resevwa", at: "20:18" }], reason: "Prèv peman an pa t koresponn ak nimewo TXN lan." } },
    { id: "t8", seed: "u-t8", kind: "depo", name: "Sarah Smith", handle: "@sarahsmith", method: "Cashapp", nit: "TRF-MSAJ08-5NRK", amount: 60, statut: "paye", at: "25 jiyè · 13:02", fee: 4.99, rate: 138,
      feed: { stage_history: [{ stage: "resevwa", at: "13:02" }, { stage: "ap_trete", at: "13:07" }, { stage: "voye", at: "13:08" }, { stage: "rive", at: "13:20" }] } },
]

/** Where Payvit itself receives — the counterparty side of every summary row. */
export const PAYVIT_ACCOUNT = { name: "Payvit HT", moncash: "50948645914", natcash: "50935088962" }

/** Same tiers as the live fee table — verified against the real
 *  TransfeFlow.tsx and payvit-admin's own fee lookup (previously out of
 *  date here: 51-75/76-100/101-150 were wrong and 151-200 was missing
 *  entirely). */
export const FEE_TIERS = [
    { min: 5, max: 25, fee: 3 },
    { min: 26, max: 50, fee: 3.5 },
    { min: 51, max: 75, fee: 5 },
    { min: 76, max: 100, fee: 7 },
    { min: 101, max: 150, fee: 8 },
    { min: 151, max: 200, fee: 11.5 },
]
export const RATE_DEPO = 138
export const RATE_RETRE = 140
export const VIT_USD_RATE = 2.5

export function calcFee(usd: number) {
    const b = Math.ceil(usd - 0.0001)
    return FEE_TIERS.find((t) => b >= t.min && b <= t.max)?.fee ?? 0
}

const SETTLED = ["paye", "complété"]
export const isSettled = (t: Tx) => SETTLED.includes(t.statut)
export const balanceUsd = TXS.filter((t) => isSettled(t) && t.kind !== "retre")
    .reduce((s, t) => s + t.amount, 0)
export const confirmedCount = TXS.filter((t) => isSettled(t) || t.statut === "approuve").length
export const vitCount = Math.floor(confirmedCount / 3)
export const vitProgress = confirmedCount % 3

export const fmtUSD = (n: number) =>
    "$" + n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
export const fmtHTG = (n: number) =>
    Math.round(n).toLocaleString("en-US") + " HTG"
export const fmt = (n: number, dp = 2) =>
    n.toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp })

export const STATUS: Record<string, { label: string; tone: "ok" | "warn" | "danger" | "info" }> = {
    paye: { label: "Peye", tone: "ok" },
    "complété": { label: "Konplete", tone: "ok" },
    en_attente: { label: "Ap trete", tone: "warn" },
    approuve: { label: "Apwouve", tone: "info" },
    rejete: { label: "Rejte", tone: "danger" },
}
